import * as React from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Divider,
  Alert,
  CircularProgress,
  Paper,
  Button,
  Stack,
  TextField,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";
import CloseOutlinedIcon from "@mui/icons-material/CloseOutlined";
import { useNavigate, useParams } from "react-router-dom";

import {
  getDeviceAssignmentById,
  listAdmissions,
  listHospitals,
  patchDeviceAssignment,
} from "../api/deviceAssignmentPagessApi";
import { ROUTES } from "../../shared/constants/routes";
import { prettyError } from "../utils/prettyError";
import { Colors } from "../../utils/Colors";

function pickFirst(...values) {
  return values.find((v) => v !== undefined && v !== null && v !== "");
}

function fmt(v) {
  if (v === undefined || v === null || v === "") return "—";
  return String(v);
}

function asString(v) {
  if (v === undefined || v === null) return "";
  return String(v);
}

function formatDateTime(value) {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  return d.toLocaleString();
}

function toDateTimeLocalValue(value) {
  if (!value) return "";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "";

  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  const hours = String(d.getHours()).padStart(2, "0");
  const minutes = String(d.getMinutes()).padStart(2, "0");

  return `${year}-${month}-${day}T${hours}:${minutes}`;
}

function toApiDateTime(value) {
  if (!value) return null;

  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return null;

  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  const hours = String(d.getHours()).padStart(2, "0");
  const minutes = String(d.getMinutes()).padStart(2, "0");
  const seconds = "00";

  return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}`;
}

// ---------------- admission helpers ----------------
function getAdmissionId(item) {
  return item?.admission_id ?? item?.id ?? item?.uuid ?? "";
}

function getAdmissionNo(item) {
  return item?.admission_no ?? item?.admission_number ?? item?.admission_code ?? "";
}

function getAdmissionPatientName(item) {
  return (
    item?.patient_name ??
    item?.patient?.full_name ??
    item?.patient?.name ??
    item?.full_name ??
    ""
  );
}

function getAdmissionPatientUhid(item) {
  return (
    item?.patient_uhid_out ??
    item?.patient_uhid ??
    item?.uhid ??
    item?.uhid_no ??
    item?.uhid_number ??
    item?.patient?.uhid ??
    ""
  );
}

function getAdmissionHospitalId(item) {
  return (
    item?.organization?.organization_id ??
    item?.organization?.id ??
    item?.organization_id ??
    item?.organization ??
    item?.hospital?.hospital_id ??
    item?.hospital?.id ??
    item?.hospital_id ??
    item?.hospital ??
    ""
  );
}

function getAdmissionHospitalName(item) {
  return item?.organization_name ?? item?.hospital_name ?? item?.hospital ?? "";
}

function getAdmissionWardName(item) {
  return item?.ward_unit ?? item?.ward_name ?? item?.ward ?? "";
}

function getAdmissionBedName(item) {
  return item?.bed_number ?? item?.bed_name ?? item?.bed ?? "";
}

// ---------------- hospital helpers ----------------
function getHospitalId(item) {
  return item?.organization_id ?? item?.hospital_id ?? item?.id ?? item?.uuid ?? "";
}

function getHospitalName(item) {
  return (
    item?.name ??
    item?.organization_name ??
    item?.hospital_name ??
    item?.title ??
    ""
  );
}

// ---------------- assignment helpers ----------------
function getAssignmentId(item) {
  return item?.device_assignment_id ?? item?.assignment_id ?? item?.id ?? item?.uuid ?? "";
}

function getAssignmentAdmissionId(item) {
  return (
    item?.admission_id ??
    item?.admission?.admission_id ??
    item?.admission?.id ??
    item?.admission ??
    ""
  );
}

function getAssignmentDeviceId(item) {
  return (
    item?.device_id ??
    item?.device?.device_id ??
    item?.device?.id ??
    item?.device ??
    ""
  );
}

function getAssignmentDeviceSerial(item) {
  return (
    item?.device_serial ??
    item?.serial_number ??
    item?.serial ??
    item?.device?.device_serial ??
    item?.device?.serial_number ??
    item?.device?.serial ??
    item?.device_code ??
    item?.device_name ??
    ""
  );
}

function makeMap(rows, getIdFn) {
  const map = {};
  (rows || []).forEach((row) => {
    const id = getIdFn(row);
    if (id !== undefined && id !== null && id !== "") {
      map[asString(id)] = row;
    }
  });
  return map;
}

function DetailRow({ label, value }) {
  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: { xs: "1fr", sm: "210px 1fr" },
        gap: 1,
        py: 1.2,
        borderBottom: `1px solid ${Colors?.border?.light || "#d9e1e7"}`,
      }}
    >
      <Typography
        sx={{
          fontWeight: 800,
          fontSize: 13,
          color: Colors?.text?.header || "#16343b",
        }}
      >
        {label}
      </Typography>
      <Typography
        sx={{
          fontSize: 14,
          color: Colors?.text?.body || "#1b3137",
        }}
      >
        {fmt(value)}
      </Typography>
    </Box>
  );
}

export default function DeviceAssignmentDetailsPage() {
  const navigate = useNavigate();
  const { assignmentId } = useParams();

  const [resolved, setResolved] = React.useState(null);
  const [loading, setLoading] = React.useState(false);
  const [saving, setSaving] = React.useState(false);
  const [error, setError] = React.useState("");
  const [success, setSuccess] = React.useState("");

  const [isEditMode, setIsEditMode] = React.useState(false);
  const [editForm, setEditForm] = React.useState({
    unassigned_at: "",
  });

  async function loadDetail() {
    setLoading(true);
    setError("");

    try {
      const [assignment, admissions, hospitals] = await Promise.all([
        getDeviceAssignmentById(assignmentId),
        listAdmissions(),
        listHospitals(),
      ]);

      const admissionsMap = makeMap(admissions, getAdmissionId);
      const hospitalsMap = makeMap(hospitals, getHospitalId);

      const admissionRef = getAssignmentAdmissionId(assignment);
      const admissionObj =
        typeof admissionRef === "object" && admissionRef !== null
          ? admissionRef
          : admissionsMap[asString(admissionRef)] || null;

      const hospitalRef = admissionObj ? getAdmissionHospitalId(admissionObj) : "";
      const hospitalObj =
        typeof hospitalRef === "object" && hospitalRef !== null
          ? hospitalRef
          : hospitalsMap[asString(hospitalRef)] || null;

      const hospitalName = pickFirst(
        assignment?.hospital_name,
        getAdmissionHospitalName(admissionObj),
        getHospitalName(hospitalObj)
      );

      const nextResolved = {
        assignmentId: getAssignmentId(assignment),
        deviceId: getAssignmentDeviceId(assignment),
        admissionNo: admissionObj ? getAdmissionNo(admissionObj) : "",
        deviceSerial: getAssignmentDeviceSerial(assignment),
        patientName: admissionObj ? getAdmissionPatientName(admissionObj) : "",
        patientUhid: admissionObj ? getAdmissionPatientUhid(admissionObj) : "",
        hospitalName,
        wardName: admissionObj ? getAdmissionWardName(admissionObj) : "",
        bedNumber: admissionObj ? getAdmissionBedName(admissionObj) : "",
        assignedAt: assignment?.assigned_at,
        unassignedAt: assignment?.unassigned_at,
        createdAt: assignment?.created_at,
        updatedAt: assignment?.updated_at,
      };

      setResolved(nextResolved);
      setEditForm({
        unassigned_at: toDateTimeLocalValue(assignment?.unassigned_at),
      });
    } catch (err) {
      setError(prettyError(err));
    } finally {
      setLoading(false);
    }
  }

  React.useEffect(() => {
    if (assignmentId) {
      loadDetail();
    }
  }, [assignmentId]);

  function handleEditClick() {
    setSuccess("");
    setError("");
    setIsEditMode(true);
    setEditForm({
      unassigned_at: toDateTimeLocalValue(resolved?.unassignedAt),
    });
  }

  function handleCancelEdit() {
    setIsEditMode(false);
    setSuccess("");
    setError("");
    setEditForm({
      unassigned_at: toDateTimeLocalValue(resolved?.unassignedAt),
    });
  }

  function handleChange(field) {
    return (event) => {
      setEditForm((prev) => ({
        ...prev,
        [field]: event.target.value,
      }));
    };
  }

  function validateEditForm() {
    if (!editForm.unassigned_at) {
      return "Unassigned At is required.";
    }

    if (resolved?.assignedAt) {
      const assignedDate = new Date(resolved.assignedAt);
      const unassignedDate = new Date(editForm.unassigned_at);

      if (!Number.isNaN(assignedDate.getTime()) && !Number.isNaN(unassignedDate.getTime())) {
        if (unassignedDate < assignedDate) {
          return "Unassigned At cannot be earlier than Assigned At.";
        }
      }
    }

    return "";
  }

  async function handleUpdate() {
    setError("");
    setSuccess("");

    const validationMessage = validateEditForm();
    if (validationMessage) {
      setError(validationMessage);
      return;
    }

    try {
      setSaving(true);

      await patchDeviceAssignment(assignmentId, {
        device_id: resolved?.deviceId,
        unassigned_at: toApiDateTime(editForm.unassigned_at),
      });

      setSuccess("Device assignment updated successfully.");
      setIsEditMode(false);
      await loadDetail();
    } catch (err) {
      setError(prettyError(err));
    } finally {
      setSaving(false);
    }
  }

  return (
    <Box sx={{ p: 2, background: Colors?.bg?.app || "#f7f9fb", minHeight: "100%" }}>
      <Box sx={{ display: "flex", justifyContent: "center" }}>
        <Card
          variant="outlined"
          sx={{
            width: "100%",
            maxWidth: 900,
            borderRadius: 3,
            borderColor: Colors?.border?.light || "#d9e1e7",
            background: Colors?.bg?.paper || "#fff",
            boxShadow: Colors?.shadow?.brand || "0 8px 18px rgba(11,60,63,0.08)",
          }}
        >
          <CardContent sx={{ p: 2.5 }}>
            <Stack
              direction={{ xs: "column", sm: "row" }}
              spacing={1}
              alignItems={{ xs: "stretch", sm: "center" }}
              justifyContent="space-between"
              sx={{ mb: 1 }}
            >
              <Button
                startIcon={<ArrowBackIcon />}
                onClick={() => navigate(ROUTES.DEVICE_ASSIGNMENT_LIST)}
              >
                Back
              </Button>

              {!loading && resolved ? (
                !isEditMode ? (
                  <Button
                    variant="contained"
                    startIcon={<EditOutlinedIcon />}
                    onClick={handleEditClick}
                    sx={{
                      borderRadius: 2,
                      textTransform: "none",
                      fontWeight: 700,
                      backgroundColor: Colors?.brand?.primary || "#0b5cab",
                      "&:hover": {
                        backgroundColor: Colors?.brand?.primaryDark || "#094a88",
                      },
                    }}
                  >
                    Edit
                  </Button>
                ) : (
                  <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
                    <Button
                      variant="outlined"
                      startIcon={<CloseOutlinedIcon />}
                      onClick={handleCancelEdit}
                      disabled={saving}
                      sx={{
                        borderRadius: 2,
                        textTransform: "none",
                        fontWeight: 700,
                      }}
                    >
                      Cancel
                    </Button>

                    <Button
                      variant="contained"
                      startIcon={
                        saving ? <CircularProgress size={18} color="inherit" /> : <SaveOutlinedIcon />
                      }
                      onClick={handleUpdate}
                      disabled={saving}
                      sx={{
                        borderRadius: 2,
                        textTransform: "none",
                        fontWeight: 700,
                        backgroundColor: Colors?.brand?.primary || "#0b5cab",
                        "&:hover": {
                          backgroundColor: Colors?.brand?.primaryDark || "#094a88",
                        },
                      }}
                    >
                      {saving ? "Updating..." : "Update"}
                    </Button>
                  </Stack>
                )
              ) : null}
            </Stack>

            <Typography
              variant="h6"
              sx={{ fontWeight: 900, color: Colors?.text?.title || "#16343b" }}
            >
              Device Assignment Details
            </Typography>

            <Divider sx={{ my: 2, borderColor: Colors?.border?.topLine || "#e5edf2" }} />

            {error ? (
              <Alert severity="error" sx={{ mb: 2 }}>
                {typeof error === "string" ? error : JSON.stringify(error)}
              </Alert>
            ) : null}

            {success ? (
              <Alert severity="success" sx={{ mb: 2 }}>
                {success}
              </Alert>
            ) : null}

            {loading ? (
              <Box
                sx={{
                  p: 3,
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  gap: 1,
                }}
              >
                <CircularProgress size={22} />
                <Typography>Loading details...</Typography>
              </Box>
            ) : resolved ? (
              <Paper
                variant="outlined"
                sx={{
                  borderRadius: 2,
                  overflow: "hidden",
                  borderColor: Colors?.border?.light || "#d9e1e7",
                  p: 2,
                }}
              >
                <DetailRow label="Assignment ID" value={resolved.assignmentId} />
                <DetailRow label="Admission Number" value={resolved.admissionNo} />
                <DetailRow label="Device Serial Number" value={resolved.deviceSerial} />
                <DetailRow label="Patient Name" value={resolved.patientName} />
                <DetailRow label="UHID" value={resolved.patientUhid} />
                <DetailRow label="Hospital Name" value={resolved.hospitalName} />
                <DetailRow label="Ward Name" value={resolved.wardName} />
                <DetailRow label="Bed Number" value={resolved.bedNumber} />
                <DetailRow label="Assigned At" value={formatDateTime(resolved.assignedAt)} />

                {!isEditMode ? (
                  <DetailRow
                    label="Unassigned At"
                    value={formatDateTime(resolved.unassignedAt)}
                  />
                ) : (
                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: { xs: "1fr", sm: "210px 1fr" },
                      gap: 1,
                      py: 1.2,
                      borderBottom: `1px solid ${Colors?.border?.light || "#d9e1e7"}`,
                      alignItems: "center",
                    }}
                  >
                    <Typography
                      sx={{
                        fontWeight: 800,
                        fontSize: 13,
                        color: Colors?.text?.header || "#16343b",
                      }}
                    >
                      Unassigned At
                    </Typography>

                    <TextField
                      fullWidth
                      type="datetime-local"
                      value={editForm.unassigned_at}
                      onChange={handleChange("unassigned_at")}
                      InputLabelProps={{ shrink: true }}
                      size="small"
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          borderRadius: 2,
                          backgroundColor: "#fff",
                        },
                      }}
                    />
                  </Box>
                )}

                <DetailRow label="Created At" value={formatDateTime(resolved.createdAt)} />
                <DetailRow label="Updated At" value={formatDateTime(resolved.updatedAt)} />
              </Paper>
            ) : null}
          </CardContent>
        </Card>
      </Box>
    </Box>
  );
}